import { useMemo } from "react";
import useCustomInputs from "@/features/customField/api/useCustomInputs";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import { isHiddenKeyField } from "@/features/customField/api/useKeyFields";

export interface GlobalCfOption {
  id: number;
  value: string;
  /** option-group name — pills render grouped sections from it */
  filterGroup: string | null;
}

export interface GlobalCfField {
  id: number;
  /** Display name (label overlay wins over machine `name`). */
  displayName: string;
  options: GlobalCfOption[];
}

/**
 * SELECTOR custom fields marked "Use as global filter" or "Key field" — the fields surfaced
 * as filter pills on the Dashboard and in Reports. Options already pass the
 * per-user visibility filter server-side (access-controlled options a user
 * can't see are stripped before they reach the client).
 */
export function useGlobalCfFields(): {
  fields: GlobalCfField[];
  isLoading: boolean;
} {
  const { connections, isLoading } = useCustomInputs();

  const fields = useMemo<GlobalCfField[]>(() => {
    const rows = (connections as any[] | undefined) ?? [];
    return rows
      .filter(
        (f) =>
          f?.type === "SELECTOR" &&
          // A KEY FIELD (useKeyFields) is always a Dashboard / Reports pill —
          // the way Country always has its pill.
          (Boolean(f?.isGlobalFilter) ||
            (Boolean(f?.isKeyField) &&
              String(f?.name ?? "").trim().toLowerCase() !== "country")) &&
          (f?.options?.length ?? 0) > 0 &&
          // A key field hidden from this viewer is no pill for them.
          !isHiddenKeyField(f) &&
          // Country / Intake already have dedicated global pills (Country
          // cascade + IntakeSelector) — never render a duplicate generic pill.
          f?.spesificTypes !== "Country" &&
          f?.spesificTypes !== "Intake",
      )
      .map((f) => ({
        id: Number(f.id),
        displayName: getCfDisplayName(f),
        options: (f.options as any[]).map((o) => ({
          id: Number(o.id),
          value: String(o.value),
          filterGroup: String(o.filterGroup ?? "").trim() || null,
        })),
      }));
  }, [connections]);

  return { fields, isLoading };
}
